import { defineCrestModule } from '@crest/platform-contracts/modules';
import { projectsProjectDetailLauncherCapability } from '@crest/module-projects/contracts/detail-launcher';
import { projectsProjectReferenceAccessHoldCapability } from '@crest/module-projects/contracts/project-reference';
import { platformOptionalActionProviderHostCapability, platformOptionalFactProviderHostCapability, platformPortalAccessScopeProviderHostCapability, platformPortalHostCapability, } from '@crest/platform-contracts/hosts';
import { platformInternalNotificationAudienceCapability, platformInternalNotificationAudienceV2Capability, platformInternalNotificationPermissionAudienceCapability, } from '@crest/platform-contracts';
import { communicationsInsuranceClaimsAdjusterCorrespondenceFactsCapability, communicationsInsuranceClaimsAdjusterCorrespondenceProviderId, } from './contracts/communications-insurance-claims-adjuster-correspondence.contract.js';
import { communicationsInsuranceClaimsNoticeDeliveryCapability, communicationsInsuranceClaimsNoticeDeliveryProviderId, } from './contracts/communications-insurance-claims-notice-delivery.contract.js';
import { communicationsInternalReportDeliveryCapability, communicationsInternalReportDeliveryProviderId, } from './contracts/communications-internal-report-delivery.contract.js';
import { communicationsTrainingAssessmentResultDeliveriesCapability, communicationsTrainingAssessmentResultEventName, } from './contracts/communications-training-assessment-result.contract.js';
import { communicationsDocumentNotificationIntentsCapability, communicationsKnowledgeSignedPolicyCopyNotificationEventName, communicationsWorkforceProtectedFormNotificationEventName, } from './contracts/communications-document-notification.contract.js';
import { communicationsPortalProjectMessageScopeProviderId, communicationsPortalProjectMessagesCapability, } from './contracts/communications-portal-project-message.contract.js';
import { communicationsCustomerPortalIntakeNotificationIntentsCapability, communicationsCustomerPortalProjectIntakeSubmittedEventName, } from './contracts/communications-customer-portal-intake.contract.js';
import { communicationsTimesheetsActionableReviewSubscriberId, communicationsTimesheetsLunchCorrectionRequestedEventName, communicationsTimesheetsMissingLunchReportReadyEventName, } from './contracts/communications-timesheets-actionable-system-notification.contract.js';
import { communicationsLegacyMessageImportCapability } from './import/communications-legacy-message-import.contract.js';
export const communicationsModuleManifest = defineCrestModule({
    id: 'communications',
    version: '1.0.0',
    kind: 'business',
    required: false,
    // Hard dependency: outbound messaging cannot function lawfully without the
    // Relationships consent record, so a release without Relationships must
    // fail at composition time.
    requires: ['platform', 'projects', 'relationships'],
    requiredCapabilities: [
        'platform.tenant-context.v1',
        'platform.module-access.v1',
        'relationships.outbound-authorization.v1',
        platformInternalNotificationAudienceCapability,
        platformInternalNotificationAudienceV2Capability,
        platformOptionalActionProviderHostCapability,
        platformOptionalFactProviderHostCapability,
        platformPortalAccessScopeProviderHostCapability,
        platformPortalHostCapability,
        'projects.detail-host.v1',
        projectsProjectDetailLauncherCapability,
        'projects.project-reference.v1',
        projectsProjectReferenceAccessHoldCapability,
        'projects.project-team-notification-audience.v1',
        'platform.system-notifications.enqueue.v1',
        'platform.current-notification-audience-hold.v1',
        'projects.project-team-notification-audience.v2',
        'platform.internal-notification-role-audience.v1',
        'projects.project-team-notification-audience.v3',
        platformInternalNotificationPermissionAudienceCapability,
        'platform.import-resolution.v1',
        'relationships.import-resolution.v1',
        'projects.import-resolution.v1',
    ],
    optionalCapabilities: [],
    optionalCapabilityFallbacks: [],
    provides: [
        'communications.messages.v1',
        communicationsInternalReportDeliveryCapability,
        communicationsInsuranceClaimsAdjusterCorrespondenceFactsCapability,
        communicationsInsuranceClaimsNoticeDeliveryCapability,
        communicationsTrainingAssessmentResultDeliveriesCapability,
        communicationsDocumentNotificationIntentsCapability,
        communicationsPortalProjectMessagesCapability,
        communicationsCustomerPortalIntakeNotificationIntentsCapability,
        'communications.project-status-notifications.v1',
        communicationsLegacyMessageImportCapability,
    ],
    permissions: [
        'communications.messages.read',
        'communications.messages.send',
        'communications.internal-deliveries.read',
        'communications.internal-deliveries.queue',
        'communications.training-results.read-own',
        'communications.training-results.read-all',
        'communications.document-notifications.read-own',
        'communications.document-notifications.read-all',
        'communications.portal-messages.read',
        'communications.portal-messages.send',
        'communications.customer-portal-intake-notifications.read-own',
        'communications.customer-portal-intake-notifications.read-all',
        'communications.project-status-notifications.read-own',
        'communications.project-status-notifications.read-all',
        'communications.import.read',
        'communications.import.execute',
        'communications.import.rollback',
    ],
    settings: [
        'communications.copy.messages',
        'communications.portal-messages.configuration',
        'communications.portal-messages.display',
        'communications.copy.portal-messages',
    ],
    copyCatalogs: ['communications.copy.messages', 'communications.copy.portal-messages'],
    events: {
        publishes: [
            'communications.message-queued.v1',
            'communications.message-suppressed.v1',
            'communications.portal-project-message-posted.v1',
            'communications.portal-project-notification-requested.v1',
        ],
        subscribes: [
            'projects.project-updated.v2',
            communicationsTrainingAssessmentResultEventName,
            communicationsWorkforceProtectedFormNotificationEventName,
            communicationsKnowledgeSignedPolicyCopyNotificationEventName,
            communicationsCustomerPortalProjectIntakeSubmittedEventName,
            'communications.portal-project-notification-requested.v1',
            communicationsTimesheetsLunchCorrectionRequestedEventName,
            communicationsTimesheetsMissingLunchReportReadyEventName,
        ],
    },
    server: {
        routes: [
            { id: 'communications.routes.messages', priority: 0 },
            { id: 'communications.routes.training-assessment-results', priority: 10 },
            { id: 'communications.routes.document-notifications', priority: 20 },
            { id: 'communications.routes.portal-project-messages', priority: 30 },
        ],
        jobs: [{ id: 'communications.jobs.purge-expired-internal-report-artifacts', priority: 20 }],
        subscribers: [{
                id: 'communications.subscribers.project-status-changed',
                requiredCapability: 'projects.project-team-notification-audience.v1',
                priority: -10,
            }, {
                id: 'communications.subscribers.training-assessment-result',
                requiredCapability: platformInternalNotificationAudienceCapability,
                priority: 0,
            }, {
                id: 'communications.subscribers.document-notification',
                requiredCapability: platformInternalNotificationAudienceV2Capability,
                priority: 10,
            }, {
                id: 'communications.subscribers.customer-portal-intake',
                requiredCapability: platformInternalNotificationAudienceV2Capability,
                priority: 20,
            }, {
                id: 'communications.subscribers.portal-project-notification',
                requiredCapability: 'projects.project-team-notification-audience.v3',
                priority: 30,
            }, {
                id: communicationsTimesheetsActionableReviewSubscriberId,
                requiredCapability: platformInternalNotificationPermissionAudienceCapability,
                priority: 40,
            }],
        slots: [{
                id: communicationsInsuranceClaimsAdjusterCorrespondenceProviderId,
                requiredCapability: platformOptionalFactProviderHostCapability,
                requiredPermission: 'communications.messages.read',
                priority: 0,
            }, {
                id: communicationsInsuranceClaimsNoticeDeliveryProviderId,
                requiredCapability: platformOptionalFactProviderHostCapability,
                requiredPermission: 'communications.messages.read',
                priority: 10,
            }, {
                id: communicationsInternalReportDeliveryProviderId,
                requiredCapability: platformOptionalActionProviderHostCapability,
                requiredPermission: 'communications.internal-deliveries.queue',
                priority: 20,
            }, {
                id: communicationsPortalProjectMessageScopeProviderId,
                requiredCapability: platformPortalAccessScopeProviderHostCapability,
                requiredPermission: 'communications.portal-messages.read',
                priority: 30,
            }],
    },
    web: {
        routes: [], navigation: [],
        slots: [{
                id: 'communications.web.projects.portal-messages',
                requiredCapability: 'projects.detail-host.v1',
                requiredPermission: 'communications.portal-messages.read',
                priority: 70,
            }],
    },
    mobile: { routes: [], slots: [] },
    portal: {
        routes: [],
        slots: [{
                id: 'communications.portal.project-messages',
                requiredCapability: platformPortalHostCapability,
                requiredPermission: 'communications.portal-messages.read',
                priority: 50,
            }],
    },
    database: {
        schema: 'communications',
        owns: [
            'communications.message_threads', 'communications.messages',
            'communications.internal_report_delivery_artifacts',
            'communications.internal_report_deliveries',
            'communications.queue_internal_report_delivery_v1',
            'communications.purge_expired_internal_report_artifacts',
            'communications.training_assessment_result_intents',
            'communications.consume_training_assessment_result_v1',
            'communications.document_notification_intents',
            'communications.consume_workforce_protected_form_notification_v1',
            'communications.consume_knowledge_signed_policy_copy_notification_v1',
            'communications.portal_project_messages',
            'communications.portal_project_message_commands',
            'communications.portal_project_notification_intents',
            'communications.portal_role_keys_are_valid',
            'communications.reject_portal_project_message_mutation',
            'communications.protect_portal_project_message_command',
            'communications.read_portal_project_messages',
            'communications.execute_portal_project_message_command',
            'communications.customer_portal_intake_notification_intents',
            'communications.reject_customer_portal_intake_notification_intent_mutation',
            'communications.consume_customer_portal_intake_submitted_v1',
            'communications.project_status_notification_intents',
            'communications.reject_project_status_intent_mutation',
            'communications.consume_project_status_changed_v1',
            'communications.consume_project_status_changed_rows_v2',
            'communications.consume_project_status_changed_v2',
            'communications.system_notification_adapter_receipts',
            'communications.reject_system_notification_adapter_receipt_mutation',
            'communications.consume_customer_portal_intake_submitted_rows_v2',
            'communications.consume_customer_portal_intake_submitted_v2',
            'communications.consume_training_assessment_result_rows_v2',
            'communications.consume_training_assessment_result_v2',
            'communications.consume_workforce_protected_form_notification_rows_v2',
            'communications.consume_workforce_protected_form_notification_v2',
            'communications.consume_knowledge_signed_policy_copy_notification_rows_v2',
            'communications.consume_knowledge_signed_policy_copy_notification_v2',
            'communications.portal_project_system_notification_intents',
            'communications.reject_portal_project_system_notification_intent_mutation',
            'communications.protect_portal_project_notification_intent_v1',
            'communications.consume_portal_project_notification_requested_rows_v1',
            'communications.consume_portal_project_notification_requested_v1',
            'communications.timesheets_actionable_system_notification_intents',
            'communications.reject_timesheets_actionable_system_notification_intent_mutation',
            'communications.consume_timesheets_lunch_correction_requested_rows_v1',
            'communications.consume_timesheets_lunch_correction_requested_v1',
            'communications.consume_timesheets_missing_lunch_report_ready_rows_v1',
            'communications.consume_timesheets_missing_lunch_report_ready_v1',
            'communications.legacy_message_import_runs',
            'communications.legacy_message_import_identities',
            'communications.legacy_message_import_receipts',
            'communications.legacy_message_import_rejects',
            'communications.legacy_message_import_changes',
            'communications.legacy_message_import_rollbacks',
            'communications.legacy_message_import_thread_aliases',
            'communications.begin_legacy_message_import_v1',
            'communications.record_legacy_message_import_reject_v1',
            'communications.apply_legacy_message_import_row_v1',
            'communications.record_legacy_message_import_thread_alias_v1',
            'communications.apply_legacy_message_import_row_v2',
            'communications.complete_legacy_message_import_v1',
            'communications.fail_legacy_message_import_v1',
            'communications.rollback_legacy_message_import_row_v1',
        ],
        readsThrough: [
            'relationships.outbound-authorization.v1',
            platformInternalNotificationAudienceCapability,
            platformInternalNotificationAudienceV2Capability,
            'projects.project-reference.v1',
            projectsProjectReferenceAccessHoldCapability,
            'projects.project-team-notification-audience.v1',
            platformPortalAccessScopeProviderHostCapability,
            'platform.system-notifications.enqueue.v1',
            'platform.current-notification-audience-hold.v1',
            'projects.project-team-notification-audience.v2',
            'platform.internal-notification-role-audience.v1',
            'projects.project-team-notification-audience.v3',
            platformInternalNotificationPermissionAudienceCapability,
            'platform.import-resolution.v1',
            'relationships.import-resolution.v1',
            'projects.import-resolution.v1',
        ],
        migrations: [
            'communications.messages_foundation',
            'communications.notice-delivery-evidence',
            'communications.internal-report-delivery',
            'communications.training-assessment-result-intents',
            'communications.documents-notification-intents',
            'communications.portal-project-messages',
            'communications.customer-portal-intake-intents',
            'communications.project-status-intents',
            'communications.project-status-system-notification-enqueue',
            'communications.retained-intent-system-notification-enqueue',
            'communications.notification-audience-completion-correction',
            'communications.portal-project-message-system-notification-adapter',
            'communications.timesheets-actionable-system-notification-adapter',
            'communications.legacy-message-import',
            'communications.legacy-email-thread-aliases',
            'communications.legacy-message-recipient-evidence',
        ],
    },
    health: ['communications.database'],
    lifecycle: {
        initialization: { timeoutMs: 5_000, failureBehavior: 'quarantine-module' },
        resources: {
            requestConcurrency: 64,
            jobConcurrency: 1,
            queueBacklogLimit: 500,
            databaseConnectionBudget: 8,
            workerIsolation: 'separate-worker',
        },
        disable: {
            data: 'retain',
            clientContributions: 'remove',
            serverRoutes: 'deny-tenant-access',
            scheduledJobs: 'skip',
            events: {
                behavior: 'drop-safe',
                durableCursor: false,
                retentionDays: 0,
                backlogLimit: 0,
                warningAt: 0,
                reconciliationProcedure: null,
                dropSafeReason: 'Project-status, Training, Documents, and Customer Portal intake notifications are time-sensitive, are not replayed after source-module or Communications disablement, and never deliver through providers. timesheets.lunch-correction-requested.v2 and timesheets.missing-lunch-report-ready.v2 are drop-safe attention hints: canonical request/report-run records and authorized Timesheets review/report APIs remain the system of record; re-enable never replays or backfills notifications.',
            },
            outboundSideEffects: 'suppress-no-retroactive-send',
            inFlight: 'finish-idempotently',
        },
        resume: {
            configuration: 'last-compatible-published',
            healthChecks: ['communications.database'],
            reconciliationProcedure: null,
            visibility: 'after-health-and-reconciliation',
        },
        uninstall: {
            executableCode: 'remove',
            data: 'retain',
            tombstone: 'retain-immutable',
            destruction: 'separate-explicit-authority',
        },
    },
    retentionPolicy: 'Communications history remains stored while disabled; internal report CSV bytes are purged after the tenant-selected Reporting retention interval.',
});
//# sourceMappingURL=communications.module.js.map